/**
 * Unified status badge for matter status, VAT settings and other indicators.
 * Semantic colours only; they deliberately ignore the firm brand colour.
 */

interface StatusBadgeProps {
  status: string;
  type?: 'matter-status' | 'vat-setting' | 'general';
  className?: string;
}

const NEUTRAL = 'bg-muted text-muted-foreground';
const INFO = 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300';
const SUCCESS = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300';
const CAUTION = 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300';
const SPECIAL = 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300';

const STYLES: Record<NonNullable<StatusBadgeProps['type']>, Record<string, string>> = {
  'matter-status': { active: SUCCESS },
  'vat-setting': {
    exempt: INFO,
    reverse: CAUTION,
    'reverse charge': CAUTION,
    override: SPECIAL,
  },
  general: { billable: SUCCESS, invoiced: INFO },
};

export function StatusBadge({ status, type = 'general', className = '' }: StatusBadgeProps) {
  const key = status.toLowerCase().replace(/_/g, ' ');
  const style = STYLES[type][key] ?? STYLES[type][status.toLowerCase()] ?? NEUTRAL;
  // Sentence case: "out_of_scope" -> "Out of scope"
  const label = key.charAt(0).toUpperCase() + key.slice(1);

  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ${style} ${className}`}
    >
      {label}
    </span>
  );
}
